import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { getLanguageAndVoiceFromVal } from "../../constants/scriptConfig";
import { setChatGptData } from "../../store/script/scriptSlice";
import {
  updateGeneratedVoice,
  updateSlideScript,
} from "../../store/slides/slidesSlice";
import { generateVoiceFromText } from "../../thunks/editor/scriptThunks";
import { getFromLocal } from "../../utils/localStorage";
import AudioPlayer from "./popovers/script/AudioPlayer";
import { Tooltip } from "./Tooltip";

const VoiceCard = ({
  setVoiceSelectorVisible,
  isUseAi,
  mismatchedVoiceAvatar,
}) => {
  const { trial, isAdmin } = useSelector((state) => state?.user);
  const [loadingVoice, setLoadingVoice] = useState(false);
  const { slides, currentSlideIndex } = useSelector((state) => state.slides);
  const { chatGptData, selectedSlideData } = useSelector(
    (state) => state.script
  );
  const { id, isDoc, isLinkVideo } = useSelector((state) => state.project);
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState([]); //[{ message: "", slideNo: 0 }]

  const userId = getFromLocal("userId");

  const currentSlide = useMemo(() => {
    return slides[currentSlideIndex];
  }, [currentSlideIndex, slides]);

  const elevenLabs = useMemo(
    () => !isUseAi && currentSlide?.elevenlabs_voice,
    [currentSlide]
  );

  const isOpenAiVoice = useMemo(
    () => currentSlide?.isOpenAiVoiceId,
    [currentSlide]
  );

  const isAccentVoice = useMemo(
    () => currentSlide?.accent_voice,
    [currentSlide]
  );

  // const isOpenAiVoice = currentSlide?.openAiVoiceId;

  const isDidAvatar = currentSlide?.objects?.some((slide) => {
    return slide.type === "avatar" && slide.url.includes("d-id");
  });

  const { voiceFile, script, voice } = useMemo(() => {
    const voiceFile = isUseAi
      ? selectedSlideData?.voiceFile
      : currentSlide?.generatedVoice;
    const script = isUseAi
      ? selectedSlideData?.originalScript || selectedSlideData?.content
      : currentSlide?.originalScript || currentSlide?.script;
    const voice = isUseAi ? selectedSlideData?.voice : currentSlide?.voice;
    return { voiceFile, script, voice };
  }, [chatGptData, currentSlide, currentSlideIndex, selectedSlideData]);

  const limitExceeded = useMemo(
    () =>
      (!isAdmin || isDoc || isLinkVideo) &&
      (script || "").length > (trial ? 150 : 600),
    [script, isAdmin, isDoc, isLinkVideo]
  );

  // const selectedVoice =
  //   isUseAi && selectedSlideData.elevenLabVoice
  //     ? {
  //         languageData: {
  //           id: selectedSlideData.elevenLabVoice.languageId,
  //           language: selectedSlideData.elevenLabVoice.language,
  //         },
  //         voiceData: {
  //           name: selectedSlideData.elevenLabVoice.name,
  //           gender: selectedSlideData.elevenLabVoice.gender,
  //         },
  //       }
  //     : isAccentVoice || isOpenAiVoice
  //     ? ""
  //     : getLanguageAndVoiceFromVal(voice);

  let selectedVoice = null;

  if (isUseAi && selectedSlideData.elevenLabVoice) {
    selectedVoice = {
      languageData: {
        id: selectedSlideData.elevenLabVoice.languageId,
        language: selectedSlideData.elevenLabVoice.language,
      },
      voiceData: {
        name: selectedSlideData.elevenLabVoice.name,
        gender: selectedSlideData.elevenLabVoice.gender,
      },
    };
  } else if (!(isAccentVoice || isOpenAiVoice)) {
    selectedVoice = getLanguageAndVoiceFromVal(voice);
  }

  const generateVoiceForUseAi = async () => {
    setLoadingVoice(true);
    setErrorMessage([]);
    const updatedChatGptData = await Promise.all(
      chatGptData.map(async (item, index) => {
        const voice = item?.voice;
        const toLang = isAccentVoice ? "" : voice.slice(0, 5);
        const { voiceFile, translatedText, error } =
          await generateVoiceFromText(
            item.originalScript,
            toLang,
            voice,
            userId,
            id,
            index,
            item.elevenLabVoice,
            isOpenAiVoice,
            isAccentVoice
          );
        if (error) {
          setErrorMessage((prev) => [
            ...(prev || []),
            { message: error, slideNo: index },
          ]);
        }
        return {
          ...item,
          generatedVoice: voiceFile,
          voiceFile,
          content: translatedText,
        };
      })
    );
    setLoadingVoice(false);
    return dispatch(setChatGptData(updatedChatGptData));
  };

  const generateVoice = async () => {
    if (!script) {
      return;
    }
    setErrorMessage([]);
    const toLang = voice.slice(0, 5);
    setLoadingVoice(true);
    const { voiceFile, translatedText, error } = await generateVoiceFromText(
      script,
      toLang,
      voice,
      userId,
      id,
      currentSlideIndex + 1,
      elevenLabs,
      isOpenAiVoice,
      isAccentVoice
    );
    setLoadingVoice(false);
    if (error) {
      setErrorMessage((prev) => [
        ...(prev || []),
        { message: error, slideNo: 0 },
      ]);
    }
    if (!voiceFile) {
      return;
    }

    dispatch(updateGeneratedVoice(voiceFile));
    return dispatch(updateSlideScript(translatedText));
  };
  return (
    <>
      {!!errorMessage.length && (
        <p className="text-xs text-vb-violet">
          {isUseAi
            ? "Please remove special character(s) from the script section of slides:" +
              errorMessage.map((e) => e.slideNo + 1).join(", ")
            : errorMessage.map((e) => e.message)}
        </p>
      )}
      <div
        className={`max-w-full mx-auto p-2 bg-vb-highlight rounded-lg border border-gray-400 ${
          isUseAi ? "!mx-5" : ""
        }`}
      >
        <div className="">
          {isUseAi &&
            "Slide " + ((+selectedSlideData.currentSelectedSlide || 0) + 1)}
          <div className="flex items-center m-2">
            <span
              className={`fi fi-${(
                (elevenLabs
                  ? elevenLabs.languageId
                  : selectedVoice?.languageData?.id
                )?.split("-")[1] || ""
              ).toLowerCase()}`}
            ></span>

            <span className="ml-3 block text-sm text-gray-700">
              <span className="font-semibold text-base">
                {elevenLabs ? elevenLabs.name : selectedVoice?.voiceData?.name}
              </span>
              ,{" "}
              {elevenLabs
                ? elevenLabs.gender
                : selectedVoice?.voiceData?.gender}
            </span>
          </div>

          <span className="ml-3 block text-sm text-gray-700">
            {elevenLabs
              ? elevenLabs.language
              : selectedVoice?.languageData?.language}
          </span>

          <div className="flex items-center justify-between p-2 ">
            <button
              className="inline-flex items-center px-1 xl:px-4 py-2 border border-black text-xs lg:text-[10px] xl:text-sm font-medium rounded-md shadow-sm text-black bg-transparent hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={() => setVoiceSelectorVisible(true)}
            >
              Change Voice
            </button>
            {!voiceFile?.mp3 || mismatchedVoiceAvatar || limitExceeded ? (
              <button
                onClick={() =>
                  isUseAi ? generateVoiceForUseAi() : generateVoice()
                }
                disabled={!script || limitExceeded || mismatchedVoiceAvatar}
                className={`${
                  !script || limitExceeded || mismatchedVoiceAvatar
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }  inline-flex items-center px-1 xl:px-4 py-2 border border-transparent text-xs lg:text-[10px] xl:text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 relative group`}
              >
                {loadingVoice ? (
                  <span className="loader-small"></span>
                ) : (
                  "Generate Voice"
                )}
                {(mismatchedVoiceAvatar || !script || limitExceeded) && (
                  <Tooltip className={""}>
                    {!script
                      ? "Please add the script"
                      : limitExceeded
                      ? trial
                        ? "Max 150 Characters allowed in free trial"
                        : isDidAvatar
                        ? "Max 200 Characters allowed."
                        : "Max 600 Characters allowed"
                      : "Selected avatar gender & voice are not match"}
                  </Tooltip>
                )}
              </button>
            ) : (
              <AudioPlayer
                key={voiceFile?.mp3}
                isLoading={loadingVoice}
                src={voiceFile?.mp3}
              />
            )}
            {/* <button className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-red-800 `}
            >
              {currentSlide.generatedVoice === "waiting"
                ? <span className="loader-small"></span>
                : "Play Script"}
            </button> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default VoiceCard;
